<template>
  <div class="user-table fbc-table-header-labels" data-testid="setup-users__table">
    <div class="user-table__datatable-wrap">
      <lb-datatableaction
        ref="dataTableAction"
        :headers="headers"
        :tabledata="tableData"
        :filter-options-data="filterOptionsData"
        :displayed-row-count="rows.length"
        :itemsperpage="itemsPerPage"
        :bulk-actions="false"
        :selectfilter="activeColumnFilters"
        :page_details="pageDetails"
        :loading="loading"
        data-testid="setup-users__datatable"
        header-bg-class="fbc-table-header-bg"
        @filter-item="onFilterItem"
        @filter-active-change="onFilterActiveChange"
        @clear-item="onClearItem"
      >
        <template #tbody>
          <tr
            v-for="item in rows"
            :key="String(item._id)"
            class="user-table__row"
            :class="{ 'user-table__row--disabled': !item.status }"
            data-testid="setup-users__row"
          >
            <template v-for="header in headers">
              <td
                v-if="header.visible && header.value === 'name'"
                :key="`${header.value}-${item._id}-name`"
                class="px-4 user-table__td user-table__td--member"
                data-testid="setup-users__name-cell"
              >
                <div class="user-table__member">
                  <div class="user-table__avatar" data-testid="setup-users__avatar">
                    {{ getMemberInitials(item.name) }}
                  </div>
                  <span class="user-table__name" data-testid="setup-users__name">
                    {{ item.name || '—' }}
                  </span>
                </div>
              </td>

              <td
                v-else-if="header.visible && header.value === 'email'"
                :key="`${header.value}-${item._id}-email`"
                class="px-4 single-line textLowercase"
                data-testid="setup-users__email-cell"
              >
                {{ item.email || '—' }}
              </td>

              <td
                v-else-if="header.visible && header.value === 'roleDisplay'"
                :key="`${header.value}-${item._id}-role`"
                class="px-4 single-line"
                data-testid="setup-users__role-cell"
              >
                <span
                  v-if="item.roleDisplay"
                  class="user-table__role-badge"
                  data-testid="setup-users__role-badge"
                >
                  {{ item.roleDisplay }}
                </span>
                <span v-else>—</span>
              </td>

              <td
                v-else-if="header.visible && header.value === 'locationDisplay'"
                :key="`${header.value}-${item._id}-location`"
                class="px-4 single-line"
                data-testid="setup-users__location-cell"
              >
                <div class="user-table__overflow-cell">
                  <OverflowList
                    :items="locationItems(item)"
                    content-class="user-table__location-tooltip"
                    data-testid="setup-users__location-overflow"
                  >
                    <template #primary="{ item: locationName }">
                      <span
                        class="user-table__location-chip"
                        data-testid="setup-users__location-chip"
                      >
                        {{ locationName }}
                      </span>
                    </template>
                  </OverflowList>
                </div>
              </td>

              <td
                v-else-if="header.visible && header.value === 'statusDisplay'"
                :key="`${header.value}-${item._id}-status`"
                class="px-4 single-line"
                data-testid="setup-users__status-cell"
              >
                <div class="d-flex align-center">
                  <StatusIndicator
                    :variant="item.status ? 'active' : 'inactive'"
                    :custom-label="item.status ? 'Active' : 'Inactive'"
                    size="sm"
                    outlined
                    data-testid="setup-users__status-indicator"
                  />
                </div>
              </td>

              <td
                v-else-if="header.visible && header.value === 'action'"
                :key="`${header.value}-${item._id}-action`"
                class="px-0 single-line border-right action-data"
                data-testid="setup-users__action-cell"
              >
                <div class="user-table__action-group">
                  <v-tooltip bottom content-class="tooltip-bottom">
                    <template #activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        class="setup-users__edit-btn user-table__edit-btn"
                        :disabled="!canEdit"
                        data-testid="setup-users__edit-btn"
                        @click="$emit('edit', item)"
                      >
                        <img
                          src="/img/icons/PenEditIcon.svg"
                          alt="Edit"
                          class="user-table__edit-icon"
                        />
                      </v-btn>
                    </template>
                    <span>Edit user</span>
                  </v-tooltip>
                  <activity-log
                    :resource-id="String(item._id)"
                    :row-description="item.name || ''"
                    data-testid="setup-users__row-activity-log"
                  />
                </div>
              </td>
            </template>
          </tr>
        </template>
      </lb-datatableaction>
    </div>
  </div>
</template>

<script>
import './UserTable.scss';
import StatusIndicator from '@/components/StatusIndicator.vue';
import ActivityLog from '@/modules/financialclose/components/ActivityLog/ActivityLog.vue';
import { OverflowList } from '@/modules/financialclose/components';
import { ITEMS_PER_PAGE, PAGE_DETAILS, TABLE_HEADERS } from '../../utils/userConstants';
import { getMemberInitials } from '../../utils/userHelpers';

export default {
  name: 'UserTable',

  components: {
    StatusIndicator,
    ActivityLog,
    OverflowList,
  },

  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    tableData: {
      type: Array,
      default: () => [],
    },
    filterOptionsData: {
      type: Array,
      default: null,
    },
    headers: {
      type: Array,
      default: () => TABLE_HEADERS,
    },
    itemsPerPage: {
      type: Number,
      default: ITEMS_PER_PAGE,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    pageDetails: {
      type: Object,
      default: () => ({ ...PAGE_DETAILS }),
    },
  },

  data() {
    return {
      activeColumnFilters: {},
      hasActiveFilters: false,
    };
  },

  methods: {
    getMemberInitials,

    /** Full location list. All locations stay a single chip with no +N badge. */
    locationItems(row) {
      const primary = row && row.locationPrimary;
      const primaryLabel = primary && primary !== '—' ? String(primary).trim() : '';
      if (!primaryLabel) return [];
      if (row.locationAll) return [primaryLabel];
      const rest = Array.isArray(row.locationOverflowItems) ? row.locationOverflowItems : [];
      return [primaryLabel, ...rest].filter((value) => {
        const text = value == null ? '' : String(value).trim();
        return text && text !== '—';
      });
    },

    onFilterItem(filters) {
      this.activeColumnFilters = filters || {};
      this.$emit('filter-change', this.activeColumnFilters);
    },

    onClearItem(filters) {
      this.activeColumnFilters = filters || {};
      this.$emit('filter-change', this.activeColumnFilters);
    },

    onFilterActiveChange(isActive) {
      this.hasActiveFilters = !!isActive;
      this.$emit('filter-active-change', this.hasActiveFilters);
    },

    clearTableFilter() {
      if (this.$refs.dataTableAction) {
        this.$refs.dataTableAction.clearFilter();
      }
    },
  },
};
</script>
